import {
	SignatureType,
	DocumentWorkflowTemplateType,
	DocumentWorkflowTemplateVisibleIn,
} from '@dealstack/shared/lib/generated';
import { z } from 'zod';

export const MasterDocumentCounterpartySchema = z.object({
	name: z.string().min(1, 'Counterparty name is required'),
	numberOfSignaturesRequired: z.number().int().min(1),
	requiresWitness: z.boolean().default(false).optional(),
});

export const MasterDocumentSignaturePlacementSchema = z.object({
	signingRoleId: z.string(),
	isWitness: z.boolean(),
	signatureIndex: z.number().int().min(0),
	pageNum: z.number().int().min(1),
	x: z.number(),
	y: z.number(),
	width: z.number().positive(),
	height: z.number().positive(),
});

export const MasterDocumentFormShape = z
	.object({
		name: z.string().min(1, 'Document name is required'),
		type: z.nativeEnum(DocumentWorkflowTemplateType),
		classifiers: z.array(z.string()).optional(),
		visibleIn: z.array(z.nativeEnum(DocumentWorkflowTemplateVisibleIn)).optional(),
		signatureType: z.nativeEnum(SignatureType),
		counterparties: z.array(MasterDocumentCounterpartySchema).optional(),
		witnessRequired: z.boolean().default(false),
		instruction: z.string().optional(),
		individualScheduleRequired: z.boolean().default(false),
		individualInstances: z.boolean().default(true),
		useManualSignaturePlacement: z.boolean().default(false),
		showStampMetadata: z.boolean().default(true),
		requiresPostDating: z.boolean().default(false),
		signaturePlacements: z.array(MasterDocumentSignaturePlacementSchema).default([]),
		documentSource: z.enum(['upload', 'template']).default('upload'),
		globalDocumentTemplateId: z.string().optional(),
	})
	.refine((data) => !(data.individualScheduleRequired && !data.individualInstances), {
		message: 'Individual schedule cannot be enabled when all investors share the same copy',
		path: ['individualScheduleRequired'],
	});
// "Exactly one source" is deliberately not a .refine here: nothing reads formState.errors,
// so a refine failure would silently block submission. It's enforced as a visible toast-guard
// in useCreateMasterDocumentDrawer's onSubmit instead, for both branches.

/**
 * Create adds one rule the edit surfaces deliberately do not carry: a new document must declare
 * at least one journey. Legacy documents predate the field and sit at `[]`; requiring it on edit
 * would block every unrelated change to them until someone classified them.
 */
export const MasterDocumentCreateShape = MasterDocumentFormShape.refine(
	(data) => (data.visibleIn?.length ?? 0) > 0,
	{
		message: 'Select at least one applicable journey',
		path: ['visibleIn'],
	}
);

export type MasterDocumentFormDto = z.infer<typeof MasterDocumentFormShape>;
export type DocumentSource = MasterDocumentFormDto['documentSource'];

export enum MASTER_DOCUMENT_FORM_FIELD {
	name = 'name',
	type = 'type',
	classifiers = 'classifiers',
	visibleIn = 'visibleIn',
	signatureType = 'signatureType',
	counterparties = 'counterparties',
	witnessRequired = 'witnessRequired',
	instruction = 'instruction',
	individualScheduleRequired = 'individualScheduleRequired',
	individualInstances = 'individualInstances',
	useManualSignaturePlacement = 'useManualSignaturePlacement',
	showStampMetadata = 'showStampMetadata',
	requiresPostDating = 'requiresPostDating',
	signaturePlacements = 'signaturePlacements',
	documentSource = 'documentSource',
	globalDocumentTemplateId = 'globalDocumentTemplateId',
}

export type MasterDocumentFormMode = 'create' | 'edit';
